import React, { useState, useEffect, memo } from "react";
import { Mic, X } from "lucide-react";
import { usePolling } from "../context/PollingContext";

const RecordingIndicator: React.FC = () => {
  const { recordingStatus } = usePolling();
  const [visible, setVisible] = useState<boolean>(true);
  const [showInitialAlert, setShowInitialAlert] = useState<boolean>(false);
  const [elapsed, setElapsed] = useState<number>(0);
  const [timerInterval, setTimerInterval] = useState<NodeJS.Timeout | null>(null);

  // Format seconds to MM:SS
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins.toString().padStart(2, "0")}:${secs.toString().padStart(2, "0")}`;
  };

  // Handle recording status changes
  useEffect(() => {
    if (recordingStatus === 'recording') {
      // Show initial alert when recording first starts
      setShowInitialAlert(true);
      setTimeout(() => setShowInitialAlert(false), 5000);
      
      // Reset and start timer
      setElapsed(0);
      const interval = setInterval(() => {
        setElapsed(prev => prev + 1);
      }, 1000);
      setTimerInterval(interval);
      
      // Always show the indicator when recording starts
      setVisible(true);
    } else {
      // Stop timer when recording stops
      if (timerInterval) {
        clearInterval(timerInterval);
        setTimerInterval(null);
      }
    }
    
    return () => {
      if (timerInterval) {
        clearInterval(timerInterval);
      }
    };
  }, [recordingStatus, timerInterval]);

  // Don't render anything if not recording
  if (recordingStatus !== 'recording' && recordingStatus !== 'paused') {
    return null;
  }

  // When minimized, show only a small icon
  if (!visible && recordingStatus === 'recording') {
    return (
      <div 
        className="fixed top-4 right-4 z-50 cursor-pointer"
        onClick={() => setVisible(true)}
      >
        <div className="flex items-center justify-center w-8 h-8 bg-red-500 rounded-full shadow-lg">
          <Mic size={16} className="text-white animate-pulse" />
        </div>
      </div>
    );
  }

  // Render the full recording indicator
  return (
    <>
      {/* Initial alert popup */}
      {showInitialAlert && (
        <div className="fixed top-4 left-1/2 transform -translate-x-1/2 z-50 bg-red-500 text-white px-4 py-2 rounded-lg shadow-lg animate-bounce">
          <div className="flex items-center">
            <Mic size={16} className="mr-2" />
            <span className="font-semibold">Recording has started!</span>
          </div>
        </div>
      )}
      
      {/* Persistent recording indicator */}
      <div 
        className={`fixed top-4 right-4 z-50 max-w-xs rounded-lg shadow-lg ${
          recordingStatus === 'recording' 
            ? 'bg-red-500 text-white' 
            : 'bg-yellow-500 text-white'
        }`}
      >
        <div className="flex items-center p-2">
          <div className="flex items-center flex-grow">
            {recordingStatus === 'recording' ? (
              <>
                <div className="w-2 h-2 rounded-full bg-white animate-pulse mr-2"></div>
                <Mic size={16} className="mr-2" />
                <span className="font-medium">REC</span>
                <span className="ml-2 font-mono">{formatTime(elapsed)}</span>
              </>
            ) : (
              <>
                <div className="w-2 h-2 rounded-full bg-white mr-2"></div>
                <Mic size={16} className="mr-2" />
                <span className="font-medium">PAUSED</span>
              </>
            )}
          </div>
          <button 
            onClick={() => setVisible(false)}
            className="ml-2 p-1 hover:bg-red-600 rounded-full"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    </>
  );
};

export default memo(RecordingIndicator);

